/**
 * @file     FlowSurface.tsx
 * @readme   ../README.md
 * @purpose  The step-flow card: a hairline-bordered surface at the base radius. Inside the FlowRenderer's own card it renders flat, so a step never draws a box inside a box.
 */
'use client';

import { forwardRef, type ComponentProps } from 'react';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { useFlowPresentation } from './FlowPresentation';
import { flowColors, flowRadius } from './flowKitTokens';

type FlowSurfaceProps = Omit<ComponentProps<typeof Box>, 'sx'> & {
  sx?: SxProps<Theme>;
  /** Draw the card even inside the shell (the shell itself uses this). */
  standalone?: boolean;
  /** Drop the border and padding below the sm breakpoint (bottom sheet). */
  flushOnMobile?: boolean;
};

export const FlowSurface = forwardRef<HTMLDivElement, FlowSurfaceProps>(function FlowSurface(
  { standalone = false, flushOnMobile = false, sx, children, ...rest },
  ref,
) {
  const { inShell, density } = useFlowPresentation();
  const flat = inShell && !standalone;
  const pad = density === 'compact' ? { xs: 4, sm: 5 } : { xs: 4, sm: 6 };

  return (
    <Box
      ref={ref}
      {...rest}
      sx={[
        {
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          minWidth: 0,
          color: flowColors.textPrimary,
        },
        flat
          ? {}
          : {
              p: flushOnMobile ? { xs: 0, sm: pad.sm } : pad,
              border: flushOnMobile ? { xs: 0, sm: `1px solid ${flowColors.borderSubtle}` } : `1px solid ${flowColors.borderSubtle}`,
              borderRadius: `${flowRadius.lg}px`,
              backgroundColor: flowColors.surfaceCard,
            },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Box>
  );
});
